import { useEffect, useRef } from 'react';

export default function AnimatedBackground() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create gentle floating particles with subtle shape changes
    const createGentleParticles = () => {
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'gentle-particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.top = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 20 + 's';
        particle.style.animationDuration = (25 + Math.random() * 15) + 's';
        particle.style.zIndex = '1';
        particle.style.pointerEvents = 'none';
        
        const shapes = ['circle', 'square', 'diamond'];
        const initialShape = shapes[Math.floor(Math.random() * shapes.length)];
        particle.setAttribute('data-shape', initialShape);
        
        container.appendChild(particle);
      }
    };

    // Create luxury floating orbs
    const createLuxuryOrbs = () => {
      for (let i = 0; i < 8; i++) {
        const orb = document.createElement('div');
        orb.className = 'luxury-orb';
        orb.style.left = Math.random() * 100 + '%';
        orb.style.top = Math.random() * 100 + '%';
        orb.style.animationDelay = Math.random() * 30 + 's';
        orb.style.animationDuration = (40 + Math.random() * 20) + 's';
        orb.style.zIndex = '1';
        orb.style.pointerEvents = 'none';
        
        container.appendChild(orb);
      }
    };

    // Create subtle geometric shapes
    const createSubtleShapes = () => {
      for (let i = 0; i < 6; i++) {
        const shape = document.createElement('div');
        shape.className = 'subtle-shape';
        shape.style.left = Math.random() * 100 + '%';
        shape.style.top = Math.random() * 100 + '%';
        shape.style.animationDelay = Math.random() * 25 + 's';
        shape.style.animationDuration = (35 + Math.random() * 15) + 's';
        shape.style.zIndex = '1';
        shape.style.pointerEvents = 'none';
        
        const shapes = ['hexagon', 'triangle', 'diamond'];
        const shapeType = shapes[Math.floor(Math.random() * shapes.length)];
        shape.setAttribute('data-shape', shapeType);
        
        container.appendChild(shape);
      }
    };

    // Create dreamy constellation
    const createDreamyConstellation = () => {
      for (let i = 0; i < 12; i++) {
        const star = document.createElement('div');
        star.className = 'dreamy-star';
        star.style.top = Math.random() * 100 + '%';
        star.style.left = Math.random() * 100 + '%';
        star.style.animationDelay = Math.random() * 15 + 's';
        star.style.animationDuration = (20 + Math.random() * 10) + 's';
        star.style.zIndex = '1';
        star.style.pointerEvents = 'none';
        
        container.appendChild(star);
      }
    };

    // Create floating luxury symbols
    const createLuxurySymbols = () => {
      const symbols = ['✦', '◆', '⬟', '◊', '✧', '⭐'];
      for (let i = 0; i < 4; i++) {
        const symbol = document.createElement('div');
        symbol.className = 'luxury-symbol';
        symbol.textContent = symbols[Math.floor(Math.random() * symbols.length)];
        symbol.style.left = Math.random() * 100 + '%';
        symbol.style.animationDelay = Math.random() * 45 + 's';
        symbol.style.animationDuration = (60 + Math.random() * 30) + 's';
        symbol.style.zIndex = '1';
        symbol.style.pointerEvents = 'none';
        container.appendChild(symbol);
      }
    };

    // Create gentle morphing blobs
    const createGentleBlobs = () => {
      for (let i = 0; i < 3; i++) {
        const blob = document.createElement('div');
        blob.className = 'gentle-morphing-blob';
        const size = 150 + Math.random() * 100;
        blob.style.width = size + 'px';
        blob.style.height = size + 'px';
        blob.style.top = Math.random() * 100 + '%';
        blob.style.left = Math.random() * 100 + '%';
        blob.style.animationDelay = Math.random() * 40 + 's';
        blob.style.animationDuration = (50 + Math.random() * 20) + 's';
        blob.style.zIndex = '1';
        blob.style.pointerEvents = 'none';
        container.appendChild(blob);
      }
    };

    // Subtle mouse trail
    const createSubtleMouseTrail = (e: MouseEvent) => {
      if (Math.random() > 0.3) return; // Only create trail 30% of the time
      
      const trail = document.createElement('div');
      trail.className = 'subtle-mouse-trail';
      trail.style.left = e.clientX - 5 + 'px';
      trail.style.top = e.clientY - 5 + 'px';
      trail.style.zIndex = '1';
      trail.style.pointerEvents = 'none';
      document.body.appendChild(trail);

      setTimeout(() => {
        trail.remove();
      }, 3000);
    };

    // Gentle click ripple
    const createGentleRipple = (e: MouseEvent) => {
      const ripple = document.createElement('div');
      ripple.className = 'gentle-ripple';
      ripple.style.left = e.clientX - 25 + 'px';
      ripple.style.top = e.clientY - 25 + 'px';
      ripple.style.zIndex = '1';
      ripple.style.pointerEvents = 'none';
      document.body.appendChild(ripple);

      setTimeout(() => {
        ripple.remove();
      }, 4000);
    };

    // Create shooting stars after user stays on page
    const createShootingStar = () => {
      const star = document.createElement('div');
      star.className = 'shooting-star';
      star.style.zIndex = '1';
      star.style.pointerEvents = 'none';
      
      // Random starting position (from different edges)
      const startSide = Math.floor(Math.random() * 4); // 0: top, 1: right, 2: bottom, 3: left
      let startX, startY, endX, endY;
      
      switch(startSide) {
        case 0: // from top
          startX = Math.random() * 100;
          startY = -5;
          endX = startX + (Math.random() * 40 - 20);
          endY = 60 + Math.random() * 40;
          break;
        case 1: // from right
          startX = 105;
          startY = Math.random() * 50;
          endX = Math.random() * 60;
          endY = startY + 40 + Math.random() * 20;
          break;
        case 2: // from bottom right to top left (classic)
          startX = 80 + Math.random() * 20;
          startY = 80 + Math.random() * 20;
          endX = Math.random() * 40;
          endY = Math.random() * 40;
          break;
        default: // from left
          startX = -5;
          startY = Math.random() * 50;
          endX = 40 + Math.random() * 60;
          endY = startY + 40 + Math.random() * 20;
      }
      
      star.style.left = startX + '%';
      star.style.top = startY + '%';
      star.style.setProperty('--end-x', endX + '%');
      star.style.setProperty('--end-y', endY + '%');
      
      // Random size and brightness
      const size = 2 + Math.random() * 3;
      star.style.width = size + 'px';
      star.style.height = size + 'px';
      
      // Random duration
      const duration = 1.5 + Math.random() * 2;
      star.style.animationDuration = duration + 's';
      
      container.appendChild(star);
      
      // Remove after animation
      setTimeout(() => {
        if (star.parentNode) {
          star.remove();
        }
      }, duration * 1000 + 100);
    };

    // Schedule shooting stars after user stays
    let stayTimer: NodeJS.Timeout;
    let shootingStarInterval: NodeJS.Timeout;
    
    const startShootingStars = () => {
      // Create shooting stars at random intervals (3-8 seconds)
      const scheduleNextStar = () => {
        const nextDelay = 3000 + Math.random() * 5000;
        shootingStarInterval = setTimeout(() => {
          createShootingStar();
          scheduleNextStar();
        }, nextDelay);
      };
      
      scheduleNextStar();
    };
    
    // Start shooting stars after 5 seconds of staying on page
    stayTimer = setTimeout(() => {
      startShootingStars();
    }, 5000);

    // Initialize all effects
    createGentleParticles();
    createLuxuryOrbs();
    createSubtleShapes();
    createDreamyConstellation();
    createLuxurySymbols();
    createGentleBlobs();

    // Add mouse event listeners with throttling
    let throttleTimer: NodeJS.Timeout | null = null;
    const handleMouseMove = (e: MouseEvent) => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        createSubtleMouseTrail(e);
        throttleTimer = null;
      }, 300);
    };

    const handleClick = (e: MouseEvent) => {
      createGentleRipple(e);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick);

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
      if (throttleTimer) clearTimeout(throttleTimer);
      if (stayTimer) clearTimeout(stayTimer);
      if (shootingStarInterval) clearTimeout(shootingStarInterval);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-0 overflow-hidden"
      style={{ zIndex: 1, pointerEvents: 'none' }}
    />
  );
}